// src/utils.js
// Utilidades compartidas para filtrado de picks.

export const MAX_AGE_SECS = 2 * 60 * 60; // 2 horas absoluto
export const STALE_SECS   = 2 * 60;      // 2 min sin update = stale

const NON_LIVE_DECISIONS = ['final', 'ft', 'ended', 'post', 'ns', 'scheduled'];

const ENDED_REASONS = [
  '≤1min restante',
  'mercado cerrado',
  'coin flip',
  'partido terminado',
  'final del partido',
  'game ended',
];

export function getPickAge(pick) {
  if (!pick || !pick.timestamp) return null;
  const t = new Date(pick.timestamp).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 1000);
}

/**
 * Parsea min_jugados "40/40" → true si ya se jugó todo el partido
 */
function gameFinished(pick) {
  const mj = pick.min_jugados || pick.minutos_jugados || '';
  if (typeof mj !== 'string') return false;
  const m = mj.match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return false;
  const played = parseInt(m[1], 10);
  const total  = parseInt(m[2], 10);
  if (!played || !total) return false;
  // Guard 2026-06-04: el partido necesita estar en Q4+ para considerarse
  // terminado. Sin esto, APS manda timer negativo en Q3 (ej -15) → la fórmula
  // del motor (nba_ou_engine.py:7504) infla minutes_played a 45/40 → el filtro
  // ocultaba partidos en Q3 que seguían jugándose. Caso: Pinheiros @ Franca.
  const period = parseInt(pick.period, 10);
  if (!isNaN(period) && period < 4) return false;
  return played >= total;
}

/**
 * Devuelve min_jugados extrapolado con wall-clock para el display.
 *
 * 2026-06-04: cuando APS está roto (timer negativo sostenido en feeds
 * como BSN), el motor clampa minutes_played a un valor congelado durante
 * todo el cuarto (ej. mp=20 toda la duración de Q3). El bot escribe
 * min_jugados_ts (timestamp ms del último cambio real de min_jugados) y
 * acá lo usamos para extrapolar el minuto del display:
 *   display_mp = mp_bot + minutos_transcurridos_desde_ts
 *   capped a period*q_dur (no pasar al siguiente cuarto antes que el bot)
 *
 * Si min_jugados_ts no está presente (back-compat con docs viejos) o si
 * el partido está stale (>5 min sin update), devuelve el valor raw.
 *
 * SOLO afecta display. El motor sigue usando minutes_played clampado
 * para sus cálculos. Esto es display puro.
 */
export function getDisplayMinJugados(pick) {
  const raw = pick && pick.min_jugados ? pick.min_jugados : '';
  if (!raw || typeof raw !== 'string') return raw;
  const m = raw.match(/^(\d+)\/(\d+)$/);
  if (!m) return raw;

  const base = parseInt(m[1], 10);
  const dur = parseInt(m[2], 10);

  // Sin timestamp del bot → fallback al valor raw
  const ts = pick.min_jugados_ts;
  if (!ts || typeof ts !== 'number') return raw;

  // El check de stale se hace sobre pick.timestamp (último update del DOC,
  // que se actualiza cada ciclo del bot ≈ cada 4s), no sobre min_jugados_ts.
  // min_jugados_ts puede ser viejo legítimamente cuando APS rompe el timer
  // (mp congelado por todo el cuarto = >5 min reales) — eso es exactamente
  // el caso que queremos extrapolar. Solo NO extrapolar si el DOC entero
  // está stale (bot dropeó el partido = >2 min sin reescribir).
  if (pick.timestamp) {
    try {
      const docMs = new Date(pick.timestamp).getTime();
      if (!isNaN(docMs)) {
        const docAge = (Date.now() - docMs) / 1000;
        if (docAge > 120) return raw;   // bot dropeó el partido
      }
    } catch (_e) { /* ignore */ }
  }

  const deltaSec = (Date.now() - ts) / 1000;
  // Si el cambio fue reciente (<30s), confiar en el bot
  if (deltaSec < 30) return raw;

  // Extrapolar: agregar 1 min por cada 60s transcurridos desde el último cambio.
  // CAP a 2 min: si APS está caído más de 2 min sin actualizar mp, el display
  // se queda en base+2 en vez de inventar minutos. Sin este cap, el listado
  // mostraba "17/40" cuando el reloj real iba en "13/40" (4 min drift).
  const extra = Math.min(Math.floor(deltaSec / 60), 2);
  if (extra <= 0) return raw;

  // Cap por cuarto activo: no pasar de period*q_dur (el bot lo actualiza
  // cuando period cambia, así que el cap respeta el cuarto actual).
  // Esto evita que el display "adelante" al cuarto siguiente antes que el bot.
  const period = parseInt(pick.period, 10);
  let cap = dur;
  if (!isNaN(period) && period >= 1 && period <= 4) {
    const qDur = Math.round(dur / 4);
    cap = Math.min(dur, period * qDur);
  }
  const capped = Math.min(base + extra, cap);
  return `${capped}/${dur}`;
}

/**
 * Parsea min_jugados "17/40" → { played: 17, total: 40 }
 * Retorna null si no se puede parsear.
 */
export function getMinutesPlayed(pick) {
  if (!pick) return null;
  const mj = pick.min_jugados || pick.minutos_jugados || '';
  if (typeof mj !== 'string') return null;
  const m = mj.match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return null;
  const played = parseInt(m[1], 10);
  const total  = parseInt(m[2], 10);
  if (isNaN(played) || isNaN(total) || total <= 0) return null;
  return { played, total };
}

/**
 * Minuto 15 es el corte para TEMPRANO (Q2 mid en FIBA, Q2 inicio en NBA).
 * Retorna true si el partido tiene muestra insuficiente.
 */
export function isEarlyGame(pick) {
  if (!pick) return false;
  const d = (pick.decision || '').toLowerCase();
  if (d.includes('early') || d.includes('temprano')) return true;
  const mp = getMinutesPlayed(pick);
  if (mp && mp.played < 15) return true;
  // Confianza de datos explícita del engine
  const dataConf = parseFloat(pick.confianza_datos || 0);
  if (dataConf > 0 && dataConf < 25) return true;
  return false;
}

/**
 * Retorna true si el partido tiene una línea válida desde Firebase.
 */
export function hasApiLine(pick) {
  if (!pick) return false;
  const linea = pick.linea ?? pick.line ?? pick.over_under;
  return linea && parseFloat(linea) > 0;
}

// ──────────────────────────────────────────────────────────────
// CACHÉ PERSISTENTE DE LÍNEAS (localStorage)
// Guarda la última línea válida vista de cada pick para que
// sobreviva refreshes del navegador.
// TTL: 5 minutos. Después de eso, se considera obsoleta.
// ──────────────────────────────────────────────────────────────

const LINE_CACHE_KEY    = 'ee_line_cache_v1';
// 30 min: defensa en profundidad para cubrir primera carga / tab nueva
// durante huecos de BetsAPI. El backend ahora preserva la última línea
// conocida en _betsapi_cache (sin TTL mientras el bot esté vivo), así
// que en condiciones normales el frontend siempre recibe `linea` válida
// — este cache solo cubre el caso edge de refresh justo cuando el bot
// pushea null.
const LINE_CACHE_TTL_MS = 30 * 60 * 1000;

function _readCache() {
  try {
    const raw = localStorage.getItem(LINE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function _writeCache(obj) {
  try {
    localStorage.setItem(LINE_CACHE_KEY, JSON.stringify(obj));
  } catch (e) {
    // localStorage lleno o deshabilitado → ignorar silenciosamente
  }
}

/**
 * Guarda la línea de un pick en localStorage.
 * Llamar esta función cada vez que llega una línea válida desde Firebase.
 */
export function saveLineToCache(pickId, linea, source, ts) {
  if (!pickId || !linea || parseFloat(linea) <= 0) return;
  const cache = _readCache();
  cache[pickId] = {
    linea: parseFloat(linea),
    source: source || 'API',
    ts: ts || Date.now(),
    cached_at: Date.now(),
  };
  // Limpiar entradas viejas (>5 min) para no acumular basura
  const cutoff = Date.now() - LINE_CACHE_TTL_MS;
  Object.keys(cache).forEach(k => {
    if ((cache[k].cached_at || 0) < cutoff) delete cache[k];
  });
  _writeCache(cache);
}

/**
 * Devuelve la línea cacheada de un pick si existe y NO expiró (5 min TTL).
 * Retorna null si no hay caché válido.
 */
export function getLineFromCache(pickId) {
  if (!pickId) return null;
  const cache = _readCache();
  const entry = cache[pickId];
  if (!entry) return null;
  // ¿Expiró?
  if (Date.now() - (entry.cached_at || 0) > LINE_CACHE_TTL_MS) {
    return null;
  }
  return entry;
}

/**
 * Retorna true si el partido recibió línea en algún momento (aunque ahora
 * esté en caché o temporalmente sin actualizar).
 *
 * Indicadores en orden de prioridad:
 *   1. Tiene línea ahora (linea/line/over_under con valor)
 *   2. Tiene línea en caché persistente (localStorage, TTL 5 min)
 *   3. Tiene linea_ts o linea_source (Firebase guardó metadata de línea)
 *   4. Tiene edge != 0 (sin línea no se puede calcular edge)
 *   5. Tiene proyección con valor (el motor solo proyecta si hay línea válida)
 */
export function hadLineEver(pick) {
  if (!pick) return false;
  if (hasApiLine(pick)) return true;
  // Caché persistente
  if (pick.id && getLineFromCache(pick.id)) return true;
  // Metadata explícita de línea
  if (pick.linea_ts || pick.line_ts) return true;
  if (pick.linea_source || pick.line_source) return true;
  // Indicadores indirectos: si hay edge o proyección calculada,
  // necesariamente hubo línea (el motor no calcula sin línea)
  const edge = parseFloat(pick.edge || 0);
  if (edge !== 0) return true;
  const proy = parseFloat(pick.proyeccion || 0);
  if (proy > 0) return true;
  return false;
}

/**
 * Retorna true si el partido lleva 2+ min jugados y NUNCA tuvo línea de API.
 * Si hubo línea aunque sea una vez (aunque ahora esté en caché), NO es sin cobertura.
 */
export function isNoCoverage(pick) {
  if (!pick) return false;
  if (hadLineEver(pick)) return false;   // tuvo línea alguna vez → tiene cobertura
  const mp = getMinutesPlayed(pick);
  if (!mp) return false;
  return mp.played >= 2;
}

export function isLive(pick) {
  if (!pick) return false;

  const age = getPickAge(pick);
  if (age !== null && age >= MAX_AGE_SECS) return false;

  // Solo filtrar por stale si NO tiene period válido activo
  // Si el bot está actualizando cada 7s, un partido en Q4 no debe desaparecer
  const period = parseInt(pick.period, 10);
  const hasValidPeriod = !isNaN(period) && period >= 1 && period <= 5;
  if (!hasValidPeriod && age !== null && age >= STALE_SECS) return false;

  if (!isNaN(period) && (period < 1 || period > 5)) return false;

  const dec = (pick.decision || '').toLowerCase();
  if (NON_LIVE_DECISIONS.some(w => dec === w || dec.startsWith(w + ' '))) return false;

  const clock = (pick.clock || '').toLowerCase().trim();
  if (clock === 'ft' || clock === 'final' || clock === 'ended' || clock === '0') return false;

  if (gameFinished(pick)) return false;

  const razon = ((pick.razon || '') + ' ' + (pick.timing_razon || '')).toLowerCase();
  if (ENDED_REASONS.some(r => razon.includes(r.toLowerCase()))) return false;

  return true;
}

/**
 * Normaliza un string de liga para comparación robusta:
 *   - Decompone diacríticos (NFKD) y los remueve.
 *   - Reemplaza nbsp/zero-width/underscores/dashes por espacio.
 *   - Colapsa espacios múltiples.
 *   - Trim + lowercase final.
 *
 * Esto cubre los casos donde dos strings "iguales" visualmente
 * difieren en bytes (typical scraping de HTML, fuentes API distintas
 * para la misma liga, etc.).
 */
export function normalizeLeague(str) {
  if (!str) return '';
  return String(str)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')  // diacriticos combinantes
    .replace(/ /g, ' ')             // non-breaking space
    .replace(/​/g, '')              // zero-width space
    .replace(/[_-]/g, ' ')              // underscores/dashes -> space
    .replace(/\s+/g, ' ')               // colapsa whitespace
    .trim()
    .toLowerCase();
}

export function matchesLeague(pick, leagueId) {
  if (leagueId === 'all' || !leagueId) return true;

  const pickLeague = normalizeLeague(pick && (pick.liga || pick.league));
  const target = normalizeLeague(leagueId);

  if (!pickLeague || !target) return false;

  // Match exacto normalizado (cubre la mayoría de los casos).
  if (pickLeague === target) return true;

  // Aliases legacy: mantenemos compatibilidad con valores viejos
  // que pueden haber quedado en localStorage o en deeplinks externos.
  if (target === 'nba'        && pickLeague.includes('nba'))        return true;
  if (target === 'nbl'        && pickLeague.includes('nbl'))        return true;
  if (target === 'euroleague' && pickLeague.includes('euro'))       return true;
  if (target === 'lnb'        && (pickLeague.includes('lnb') || pickLeague.includes('chile'))) return true;
  if (target === 'nbb'        && (pickLeague.includes('nbb') || pickLeague.includes('brasil'))) return true;
  if (target === 'acb'        && pickLeague.includes('acb'))        return true;
  if (target === 'bsn'        && pickLeague.includes('bsn'))        return true;

  return false;
}


/**
 * Etiqueta de período según deporte (2026-09-04).
 * Handball son 2 mitades de 30' con minuto corrido 0-60 → "1T · min 15".
 * Básquet mantiene cuartos → "Q2 · min 16/40".
 * `pick.match_type === 'handball'` lo escribe el tracker de handball.
 */
export function esHandballPick(pick) {
  if (!pick) return false;
  return pick.match_type === 'handball' || pick.sport === 'handball'
    || /handball/i.test(String(pick.liga || pick.league || ''));
}

export function periodLabel(pick) {
  if (!pick) return '';
  if (esHandballPick(pick)) {
    const mitad = Number(pick.period) === 2 ? '2T' : '1T';
    const min = pick.minuto != null ? Math.round(pick.minuto) : null;
    return min != null ? `${mitad} · min ${min}` : mitad;
  }
  return `Q${pick.period || '?'}`;
}
